import React from 'react';
import { Table, Button, Tag, Input, App, Card, Dropdown, Modal } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  LockOutlined,
  SearchOutlined,
  MoreOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';

const { Search } = Input;

const UserManagementTable = ({
  users,
  loading,
  currentUserEmail,
  tenants,
  onEdit,
  onResetPassword,
  onDelete
}) => {
  const { message } = App.useApp();
  const [searchText, setSearchText] = React.useState('');

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'purple';
      case 'tenant_admin':
        return 'blue';
      default:
        return 'green';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'tenant_admin':
        return 'Tenant Admin';
      default:
        return 'User';
    }
  };

  const getTenantName = (tenantId) => {
    if (!tenantId) return 'N/A';
    const tenant = tenants?.find(t => t.id === tenantId);
    return tenant ? tenant.name : tenantId;
  };

  // Filter users based on search
  const filteredUsers = users.filter(user =>
    `${user.first_name} ${user.last_name} ${user.email}`.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'First Name',
      dataIndex: 'first_name',
      key: 'first_name',
      sorter: (a, b) => a.first_name.localeCompare(b.first_name),
      width: 150,
    },
    {
      title: 'Last Name',
      dataIndex: 'last_name',
      key: 'last_name',
      sorter: (a, b) => a.last_name.localeCompare(b.last_name),
      width: 150,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 250,
      ellipsis: true,
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 150,
      render: (role) => (
        <Tag color={getRoleColor(role)}>
          {getRoleLabel(role)}
        </Tag>
      ),
      filters: [
        { text: 'Super Admin', value: 'super_admin' },
        { text: 'Tenant Admin', value: 'tenant_admin' },
        { text: 'User', value: 'user' },
      ],
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      width: 180,
      render: (tenantId) => getTenantName(tenantId),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => {
        const isCurrentUser = record.email === currentUserEmail;

        const menuItems = [
          {
            key: 'edit',
            label: 'Edit',
            icon: <EditOutlined />,
            disabled: isCurrentUser,
            onClick: () => onEdit(record)
          },
          {
            key: 'reset',
            label: 'Reset Password',
            icon: <LockOutlined />,
            disabled: isCurrentUser,
            onClick: () => {
              onResetPassword(record.email);
              message.info(`Password reset email will be sent to ${record.email}`);
            }
          },
          {
            type: 'divider'
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteOutlined />,
            danger: true,
            disabled: isCurrentUser,
            onClick: () => {
              Modal.confirm({
                title: 'Delete User',
                icon: <ExclamationCircleOutlined />,
                content: `Are you sure you want to delete ${record.first_name} ${record.last_name}?`,
                okText: 'Yes, Delete',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => onDelete(record.email)
              });
            }
          }
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            disabled={isCurrentUser && menuItems.every(item => item.disabled)}
          >
            <Button
              icon={<MoreOutlined />}
              size="small"
            >
              Actions
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <Search
          placeholder="Search by name or email"
          allowClear
          enterButton={<SearchOutlined />}
          size="large"
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: 400 }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={filteredUsers}
        loading={loading}
        rowKey="email"
        pagination={{
          total: filteredUsers.length,
          showTotal: (total) => `Total ${total} users`,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ['10', '25', '50', '100'],
          defaultPageSize: 25,
        }}
        size="middle"
      />
    </Card>
  );
};

export default UserManagementTable;
